import { useState, useMemo } from "react";
import {
  Check,
  ChevronsUpDown,
  Search,
  Building2,
  User,
  Users,
  Crown,
  Link2Off,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLessors, useOwnerProfiles } from "@/hooks/v2/useLessors";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import type { Lessor } from "@/types/lessor";
import type { OwnerProfile } from "@/hooks/v2/useLessors";

type Props = {
  value: string | null;
  onChange: (next: string | null) => void;
  placeholder?: string;
  allowNone?: boolean;
  noneLabel?: string;
  className?: string;
  triggerClassName?: string;
  disabled?: boolean;
  /**
   * COL-252: when provided, property owners are directly selectable (even with
   * no linked lessor) and selecting one calls this with the owner's user_id.
   * The consumer stores it as apartment.owner_user_id — the owner is treated as
   * the lessor by default. Selecting a lessor clears the owner and vice-versa.
   */
  ownerValue?: string | null;
  onSelectOwner?: (userId: string, owner: OwnerProfile) => void;
};

const TYPE_CONFIG: Record<
  string,
  { label: string; labelKey: string; icon: React.ElementType; color: string }
> = {
  Company: {
    label: "Company",
    labelKey: "admin.lessor.type.company",
    icon: Building2,
    color: "bg-blue-50 text-blue-700 border-blue-200",
  },
  Individual: {
    label: "Individual",
    labelKey: "admin.lessor.type.individual",
    icon: User,
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  GbR: {
    // "GbR" (Gesellschaft bürgerlichen Rechts) is a German legal entity
    // abbreviation — left as-is per the batch's vocabulary rules.
    label: "GbR",
    labelKey: "admin.lessor.type.gbr",
    icon: Users,
    color: "bg-violet-50 text-violet-700 border-violet-200",
  },
};

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

function Avatar({
  name,
  type,
  variant = "lessor",
}: {
  name: string;
  type?: string;
  variant?: "lessor" | "owner";
}) {
  if (variant === "owner") {
    return (
      <span className="inline-flex items-center justify-center rounded-full w-7 h-7 text-[11px] font-semibold shrink-0 border bg-amber-50 text-amber-700 border-amber-200">
        {initials(name)}
      </span>
    );
  }
  const config = type ? TYPE_CONFIG[type] : undefined;
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-full w-7 h-7 text-[11px] font-semibold shrink-0 border",
        config?.color ?? "bg-gray-100 text-gray-600 border-gray-200"
      )}
    >
      {initials(name)}
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  const { t } = useLanguage();
  const config = TYPE_CONFIG[type];
  const Icon = config?.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border shrink-0",
        config?.color ?? "bg-gray-100 text-gray-600 border-gray-200"
      )}
    >
      {Icon && <Icon className="h-2.5 w-2.5" />}
      {config ? t(config.labelKey, config.label) : type}
    </span>
  );
}

function SectionHeader({
  icon: Icon,
  label,
  count,
}: {
  icon: React.ElementType;
  label: string;
  count: number;
}) {
  return (
    <div className="flex items-center gap-1.5 px-3 pt-2 pb-1">
      <Icon className="h-3 w-3 text-muted-foreground" />
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="text-[10px] text-muted-foreground/60">({count})</span>
    </div>
  );
}

function LessorRow({
  lessor,
  selected,
  onSelect,
}: {
  lessor: Lessor;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg transition-colors",
        "hover:bg-accent focus:bg-accent focus:outline-none",
        selected && "bg-accent"
      )}
    >
      <Avatar name={lessor.name} type={lessor.type} />
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium truncate">
          {lessor.name}
        </span>
        {lessor.city_postal && (
          <span className="block text-[11px] text-muted-foreground truncate">
            {lessor.city_postal}
          </span>
        )}
      </span>
      <div className="flex items-center gap-1.5 shrink-0">
        <TypeBadge type={lessor.type} />
        {selected && <Check className="h-3.5 w-3.5 text-primary" />}
      </div>
    </button>
  );
}

function OwnerRow({
  owner,
  linkedLessorName,
  ownerMode,
  selected,
  onSelect,
}: {
  owner: OwnerProfile;
  linkedLessorName: string | null;
  /** COL-252: owners are directly selectable (owner = lessor by default). */
  ownerMode: boolean;
  selected: boolean;
  onSelect: (() => void) | null;
}) {
  const { t } = useLanguage();
  const linked = owner.lessor_id !== null;
  // In owner-mode any owner is selectable. In the legacy lessor-mode, only
  // owners that already link to a lessor can be picked (they resolve to it).
  const clickable = ownerMode ? !!onSelect : linked;
  const linkedLessor =
    linkedLessorName ??
    t("admin.lessor.combobox.linkedLessorFallback", "linked lessor");
  const subtitle = ownerMode
    ? linked
      ? `${t("admin.lessor.combobox.ownerOverridesToLessorPrefix", "Owner · overrides to lessor:")} ${linkedLessor}`
      : t(
          "admin.lessor.combobox.ownerIsLessorByDefault",
          "Owner is the lessor by default"
        )
    : linked
      ? `${t("admin.lessor.combobox.selectingLinksToLessorPrefix", "Selecting links to lessor:")} ${linkedLessor}`
      : t("admin.lessor.combobox.noLessorLinked", "No lessor linked");
  return (
    <div
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-lg",
        clickable ? "hover:bg-accent cursor-pointer" : "opacity-60",
        selected && "bg-accent"
      )}
      onClick={clickable ? (onSelect ?? undefined) : undefined}
      role={clickable ? "button" : undefined}
    >
      <Avatar name={owner.name} variant="owner" />
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium truncate">{owner.name}</span>
        <span className="block text-[11px] text-muted-foreground truncate">
          {subtitle}
        </span>
      </span>
      <div className="flex items-center gap-1.5 shrink-0">
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border shrink-0",
            linked
              ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-gray-50 text-gray-400 border-gray-200"
          )}
        >
          {linked ? (
            <Crown className="h-2.5 w-2.5" />
          ) : (
            <Link2Off className="h-2.5 w-2.5" />
          )}
          {t("admin.lessor.combobox.ownerBadge", "Owner")}
        </span>
        {selected && <Check className="h-3.5 w-3.5 text-primary" />}
      </div>
    </div>
  );
}

export function LessorCombobox({
  value,
  onChange,
  placeholder,
  allowNone = true,
  noneLabel,
  className,
  triggerClassName,
  disabled,
  ownerValue = null,
  onSelectOwner,
}: Props) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { data: lessors = [], isLoading: lessorsLoading } = useLessors(search);
  const { data: owners = [], isLoading: ownersLoading } =
    useOwnerProfiles(search);
  const resolvedPlaceholder =
    placeholder ?? t("admin.lessor.combobox.selectOwner", "Select owner…");
  const resolvedNoneLabel =
    noneLabel ?? t("admin.lessor.combobox.noOwnerLinked", "No owner linked");

  const ownerMode = typeof onSelectOwner === "function";
  const isLoading = lessorsLoading || ownersLoading;

  const selected = value ? (lessors.find((l) => l.id === value) ?? null) : null;
  const selectedOwner =
    ownerValue != null
      ? (owners.find((o) => o.user_id === ownerValue) ?? null)
      : null;
  const triggerLabel =
    selectedOwner?.name ??
    selected?.name ??
    (value || ownerValue
      ? t("admin.lessor.combobox.loading", "Loading…")
      : resolvedPlaceholder);

  const grouped = useMemo(() => {
    const order = ["Company", "Individual", "GbR"];
    const map = new Map<string, Lessor[]>();
    for (const l of lessors) {
      const key = l.type ?? "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(l);
    }
    return order
      .filter((k) => map.has(k))
      .concat([...map.keys()].filter((k) => !order.includes(k)))
      .map((k) => ({ type: k, items: map.get(k)! }));
  }, [lessors]);

  const lessorNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of lessors) m.set(l.id, l.name);
    return m;
  }, [lessors]);

  const hasResults = lessors.length > 0 || owners.length > 0;
  const totalCount = lessors.length + owners.length;

  return (
    <div className={className}>
      <Popover
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setSearch("");
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "w-full justify-between font-normal h-10 px-3",
              !selected && !value && "text-muted-foreground",
              triggerClassName
            )}
          >
            <div className="flex items-center gap-2 min-w-0">
              {selected && <Avatar name={selected.name} type={selected.type} />}
              <span className="truncate text-sm">{triggerLabel}</span>
              {selected && <TypeBadge type={selected.type} />}
            </div>
            {(selected || selectedOwner) && !disabled && (
              // Clear the current selection. A <span> (not <button>) avoids
              // nesting interactive elements inside the trigger button; we stop
              // propagation so clicking it doesn't open the popover. onChange(null)
              // clears the lessor — and in owner-mode the apartment consumer's
              // onChange also clears owner_user_id.
              <span
                role="button"
                tabIndex={0}
                aria-label={t("admin.lessor.combobox.remove", "Remove")}
                title={t("admin.lessor.combobox.remove", "Remove")}
                className="ml-1 inline-flex shrink-0 items-center justify-center rounded-md p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onChange(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    e.preventDefault();
                    onChange(null);
                  }
                }}
              >
                <X className="h-4 w-4" />
              </span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="p-0 w-[--radix-popover-trigger-width] min-w-[300px] shadow-lg border rounded-xl"
          align="start"
          sideOffset={4}
        >
          <div className="flex flex-col rounded-xl overflow-hidden">
            {/* Search bar */}
            <div className="flex items-center gap-2 px-3 py-2.5 border-b bg-muted/30 shrink-0">
              <Search className="h-4 w-4 text-muted-foreground shrink-0" />
              <input
                autoFocus
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t(
                  "admin.lessor.combobox.searchOwners",
                  "Search owners…"
                )}
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="text-muted-foreground hover:text-foreground transition-colors text-xs"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Scrollable list */}
            <div className="relative">
              <div
                className="max-h-[260px] overflow-y-auto overscroll-contain p-1.5 space-y-0.5 scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgb(0 0 0 / 0.12) transparent",
                  WebkitOverflowScrolling: "touch",
                }}
                onWheel={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
              >
                {/* None option */}
                {allowNone && (
                  <button
                    type="button"
                    onClick={() => {
                      onChange(null);
                      setOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg transition-colors text-sm",
                      "hover:bg-accent focus:bg-accent focus:outline-none text-muted-foreground",
                      value === null && "bg-accent text-foreground font-medium"
                    )}
                  >
                    <span className="inline-flex items-center justify-center rounded-full w-7 h-7 bg-muted border border-dashed border-muted-foreground/30 shrink-0">
                      <span className="text-[10px] text-muted-foreground">
                        —
                      </span>
                    </span>
                    <span className="flex-1">{resolvedNoneLabel}</span>
                    {value === null && (
                      <Check className="h-3.5 w-3.5 text-primary" />
                    )}
                  </button>
                )}

                {isLoading && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {t("admin.lessor.combobox.searching", "Searching…")}
                  </div>
                )}

                {!isLoading && !hasResults && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {search
                      ? `${t("admin.lessor.combobox.noResultsForPrefix", "No results for")} "${search}"`
                      : t(
                          "admin.lessor.combobox.noOwnersFound",
                          "No owners found."
                        )}
                  </div>
                )}

                {/* Property owners group — shown first so it's immediately visible */}
                {!isLoading && owners.length > 0 && (
                  <div>
                    <SectionHeader
                      icon={Crown}
                      label={t(
                        "admin.lessor.combobox.propertyOwners",
                        "Property Owners"
                      )}
                      count={owners.length}
                    />
                    {owners.map((o) => (
                      <OwnerRow
                        key={o.user_id}
                        owner={o}
                        ownerMode={ownerMode}
                        linkedLessorName={
                          o.lessor_id
                            ? (lessorNameById.get(o.lessor_id) ?? null)
                            : null
                        }
                        selected={
                          ownerMode
                            ? ownerValue === o.user_id
                            : !!(value && o.lessor_id && value === o.lessor_id)
                        }
                        onSelect={
                          ownerMode
                            ? () => {
                                onSelectOwner!(o.user_id, o);
                                setOpen(false);
                              }
                            : o.lessor_id
                              ? () => {
                                  onChange(o.lessor_id!);
                                  setOpen(false);
                                }
                              : null
                        }
                      />
                    ))}
                  </div>
                )}

                {/* Lessor groups */}
                {!isLoading &&
                  grouped.map(({ type, items }) => (
                    <div key={type}>
                      <SectionHeader
                        icon={TYPE_CONFIG[type]?.icon ?? User}
                        label={
                          type === "GbR"
                            ? "GbR"
                            : type === "Company"
                              ? t(
                                  "admin.lessor.combobox.group.company",
                                  "Companies"
                                )
                              : type === "Individual"
                                ? t(
                                    "admin.lessor.combobox.group.individual",
                                    "Individuals"
                                  )
                                : t(
                                    "admin.lessor.combobox.group.other",
                                    "Other"
                                  )
                        }
                        count={items.length}
                      />
                      {items.map((l) => (
                        <LessorRow
                          key={l.id}
                          lessor={l}
                          selected={value === l.id}
                          onSelect={() => {
                            onChange(l.id);
                            setOpen(false);
                          }}
                        />
                      ))}
                    </div>
                  ))}
              </div>
              {/* Fade at bottom to signal more content */}
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-background to-transparent" />
            </div>

            {/* Footer */}
            {!isLoading && hasResults && (
              <div className="border-t px-3 py-1.5 bg-muted/20 flex items-center gap-2 shrink-0">
                <span className="text-[10px] text-muted-foreground">
                  {`${lessors.length} ${
                    lessors.length !== 1
                      ? t("admin.lessor.combobox.lessorsPlural", "lessors")
                      : t("admin.lessor.combobox.lessorSingular", "lessor")
                  }`}
                  {owners.length > 0 &&
                    ` · ${owners.length} ${
                      owners.length !== 1
                        ? t("admin.lessor.combobox.ownersPlural", "owners")
                        : t("admin.lessor.combobox.ownerSingular", "owner")
                    }`}
                  {search &&
                    ` ${t("admin.lessor.combobox.matchingSearchPrefix", "matching")} "${search}"`}
                </span>
                {totalCount > 0 && (
                  <span className="ml-auto text-[10px] text-muted-foreground/50">
                    {`${totalCount} ${t("admin.lessor.combobox.totalSuffix", "total")}`}
                  </span>
                )}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
